import React from 'react';
import styled from 'styled-components';
import { media } from '../theme/tokens';

export interface HorizontalBarRow {
  label: string;
  value: number;
  previousValue?: number;
}

interface HorizontalBarChartProps {
  data: HorizontalBarRow[];
  /** Format function for the value column */
  formatValue?: (value: number) => string;
  /** Legend labels [current, previous]. If omitted and previousValue present, defaults to ['Current', 'N-1'] */
  legend?: [string, string];
  className?: string;
}

export const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
  data,
  formatValue = (v) => v.toLocaleString('fr-FR'),
  legend,
  className
}) => {
  const maxValue = Math.max(
    ...data.map((r) => Math.max(r.value, r.previousValue ?? 0)),
    1
  );
  const hasPrevious = data.some((r) => r.previousValue != null);

  return (
    <Wrapper className={className}>
      {data.map((row, i) => (
        <Row key={i}>
          <Label>{row.label}</Label>
          <BarGroup>
            <Track>
              <Bar $width={(row.value / maxValue) * 100} $variant="current" />
            </Track>
            {hasPrevious && (
              <Track>
                <Bar
                  $width={((row.previousValue ?? 0) / maxValue) * 100}
                  $variant="previous"
                />
              </Track>
            )}
          </BarGroup>
          <Value>{formatValue(row.value)}</Value>
        </Row>
      ))}
      {hasPrevious && (
        <Legend>
          <LegendDot $variant="current" /> {legend?.[0] ?? 'Current'}
          <LegendDot $variant="previous" /> {legend?.[1] ?? 'N-1'}
        </Legend>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div``;

const Row = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 60px;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;

  ${media.md} {
    grid-template-columns: 160px 1fr 80px;
  }
`;

const Label = styled.div`
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BarGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const Track = styled.div`
  height: 10px;
  background: var(--line);
  border-radius: 4px;
  overflow: hidden;
`;

const Bar = styled.div<{ $width: number; $variant: 'current' | 'previous' }>`
  height: 100%;
  border-radius: 4px;
  width: ${({ $width }) => Math.max($width, 1)}%;
  background: ${({ $variant }) =>
    $variant === 'current' ? 'var(--terracotta)' : 'var(--ink-faint)'};
  transition: width 0.3s ease;
`;

const Value = styled.div`
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  text-align: right;
  color: var(--ink-muted);
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
  font-size: 11px;
  color: var(--ink-faint);
`;

const LegendDot = styled.span<{ $variant: 'current' | 'previous' }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $variant }) =>
    $variant === 'current' ? 'var(--terracotta)' : 'var(--ink-faint)'};
  margin-left: 8px;

  &:first-child {
    margin-left: 0;
  }
`;
